// Snapshots / Period detail page (A/S/U).
//
// Lists every snapshot in a period with the joined display name, balance,
// metadata blob highlights, and a click-through to the per-snapshot view.
// Provides CSV export of the period (server-side TSV would be ideal; for the
// POC scope we render the table client-side and let the browser print/copy).

import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { snapshots as snapshotsApi } from '@/lib/api';
import { formatBalance } from '@/lib/format';
import type {
  MonthlySnapshotListItem,
  SnapshotsForPeriodResponse,
} from '@frt/shared-types';
import { csvCell } from '@frt/shared-types';

export function SnapshotPeriodDetailPage() {
  const { period } = useParams({ from: '/snapshots/$period' as never }) as { period: string };
  const query = useQuery({
    queryKey: ['snapshots', period] as const,
    queryFn: () => snapshotsApi.forPeriod(period),
  });

  const csv = useMemo(() => {
    const data = query.data as SnapshotsForPeriodResponse | undefined;
    if (!data) return '';
    const header = [
      'snapshot_id',
      'account_id',
      'period',
      'display_name',
      'balance_amount',
      'balance_source',
      'frt_customer_code',
      'tester_name',
      'account_type',
      'product_type',
      'frt_category',
      'tiller_status',
      'on_point_tester_name',
      'frozen_at',
    ].join(',');
    const lines = data.data.map((s) =>
      [
        s.id,
        s.account_id,
        s.period,
        csvEscape(s.account_display_name),
        s.balance_amount,
        s.balance_source,
        csvEscape(s.metadata_snapshot.frt_customer_code),
        csvEscape(s.metadata_snapshot.tester_name),
        csvEscape(s.metadata_snapshot.account_type),
        csvEscape(s.metadata_snapshot.product_type),
        csvEscape(s.metadata_snapshot.frt_category ?? ''),
        s.metadata_snapshot.tiller_status,
        csvEscape(s.metadata_snapshot.on_point_tester_name ?? ''),
        s.frozen_at,
      ].join(','),
    );
    return [header, ...lines].join('\n');
  }, [query.data]);

  if (query.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (query.error) {
    return (
      <div className="text-sm text-red-600">
        Failed to load period: {(query.error as Error).message}
      </div>
    );
  }
  const data = query.data as SnapshotsForPeriodResponse;
  const empty = data.data.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Snapshots — <span className="font-mono">{period}</span>
          </h1>
          <p className="text-sm text-slate-500">
            {empty
              ? 'No snapshots for this period yet.'
              : `Frozen at ${new Date(data.frozen_at!).toLocaleString()}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            disabled={empty}
            onClick={() => downloadCsv(`snapshots-${period}.csv`, csv)}
          >
            Download CSV
          </Button>
        </div>
      </div>

      {empty ? (
        <Card>
          <CardBody>
            <p className="text-sm text-slate-600">
              The period <span className="font-mono">{period}</span> hasn't been frozen yet.
            </p>
            <Link
              to="/snapshots/$period/preview"
              params={{ period }}
              className="mt-2 inline-block text-brand-700 hover:underline"
            >
              Open preview
            </Link>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {data.totals.count} snapshots — total balance{' '}
              {formatBalance(data.totals.balance_amount_sum)}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-2 pr-3">Account</th>
                    <th className="py-2 pr-3">Customer</th>
                    <th className="py-2 pr-3">Tester</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Tiller</th>
                    <th className="py-2 pr-3 text-right">Balance</th>
                    <th className="py-2 pr-3">Source</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((s: MonthlySnapshotListItem) => (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="py-2 pr-3 font-mono text-xs">
                        <Link
                          to="/snapshots/snapshot/$id"
                          params={{ id: String(s.id) }}
                          className="text-brand-700 hover:underline"
                        >
                          {s.account_display_name}
                        </Link>
                      </td>
                      <td className="py-2 pr-3">{s.metadata_snapshot.frt_customer_code}</td>
                      <td className="py-2 pr-3">{s.metadata_snapshot.tester_name}</td>
                      <td className="py-2 pr-3">{s.metadata_snapshot.product_type}</td>
                      <td className="py-2 pr-3">{s.metadata_snapshot.tiller_status}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {formatBalance(s.balance_amount)}
                      </td>
                      <td className="py-2 pr-3">{s.balance_source}</td>
                      <td className="py-2 pr-3 text-right">
                        <Link
                          to="/snapshots/snapshot/$id"
                          params={{ id: String(s.id) }}
                          className="text-brand-700 hover:underline"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function csvEscape(s: string): string {
  return csvCell(s);
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
